import { Inject, Injectable, Optional } from '@angular/core';
import { batch, ClientConfigService, identity, isTruthy, MpxCache, MpxCacheSnapshot, uniqueBy } from 'osn-shared';
import { catchError, forkJoin, mergeMap, Observable, of, OperatorFunction, Subject, switchMap, tap } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MPX_CACHE_FACTORY_TOKEN } from '../../../../mpx-resolver-cache-factory-token';
import { ContentMpxService } from './content-mpx.service';
import { MpxEpisodeContentResponse, MpxGuidAndIdResponse, MpxProgramResponse, MpxSeriesContentResponse } from './data/mpx-program-response';
import { MpxResponse } from './data/mpx-response';
import { MpxSeries } from './data/mpx-series';

const MPX_CHARACTER_LIMIT = 1500;
const BATCH_DEBOUNCE_MS = 25;

@Injectable({ providedIn: 'root' })
export class MpxResolverService {
  private firstCacheDropAttempted = false;
  private mpxCache = this.createMpxCache();

  private readonly guidFetchStream$ = new Subject<string>();
  private readonly mpxGenres$ = this.configService.configAvailable$.pipe(
    switchMap(() =>
      this.mpxService.getMpxGenres$().pipe(
        map((result) => result.entries),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    )
  );

  constructor(
    private readonly mpxService: ContentMpxService,
    private readonly configService: ClientConfigService,
    @Optional() @Inject(MPX_CACHE_FACTORY_TOKEN) private readonly mpxCacheFactory: () => MpxCache
  ) {
    this.guidFetchStream$
      .pipe(
        batch(BATCH_DEBOUNCE_MS),
        mergeMap((guids) => forkJoin([of(guids), this.getMpxProgramsByGuidsRequest$(guids)]))
      )
      .subscribe(([programGuids, programs]) => {
        this.mpxCache.cacheProgramValues(programs);

        const missingGuids = programGuids.subtract(
          programs.map((it) => it.guid),
          identity()
        );

        missingGuids.forEach((guid) => this.mpxCache.setNullProgramCache(guid));
        return forkJoin(programGuids.map((it) => this.mpxCache.getCachedProgram$(it))).pipe(map((result) => result.filterTruthy()));
      });
  }

  public contentIdToContentSubjectId(id: string): string {
    const splitId = id.split('/');
    return splitId[splitId.length - 1];
  }

  public resolveProgram$(programGuid: string): Observable<MpxProgramResponse> {
    const cacheInstance = this.mpxCache;

    return this.resolvePrograms$([programGuid]).pipe(
      map((result) => result.firstOrNull()),
      tap((result) => {
        if (result) {
          cacheInstance.cacheProgramValues([result]);
        } else {
          cacheInstance.setNullProgramCache(programGuid);
          console.warn(`MPX has no program with GUID ${programGuid}`);
        }
      })
    );
  }

  public resolvePrograms$(guids: string[]): Observable<MpxProgramResponse[]> {
    if (!guids.length) {
      return of([]);
    }

    const result$ = this._resolvePrograms$(guids, this.mpxCache).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    result$.subscribe();
    return result$;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public resolveProgramsByIds$(ids: string[]): Observable<MpxProgramResponse[]> {
    const cacheInstance = this.mpxCache;
    if (!ids.length) {
      return of([]);
    }

    const nonCachedIds: string[] = [];

    for (const id of ids) {
      if (!cacheInstance.hasProgramById(id)) {
        nonCachedIds.push(id);
        cacheInstance.prepareProgramByIdCache$(id);
      }
    }

    if (nonCachedIds.length) {
      this.mpxService
        .getGuidsByIds$(nonCachedIds)
        .pipe(
          map((result) => {
            // If only a single ID is in the list,
            if ((result as MpxResponse<MpxGuidAndIdResponse>).entries instanceof Array) {
              return (result as MpxResponse<MpxGuidAndIdResponse>).entries.map((it) => it.guid);
            } else {
              return [(result as MpxGuidAndIdResponse).guid];
            }
          }),
          this.guidsIntoCachedPrograms(cacheInstance)
        )
        .subscribe((result) => {
          result
            .filter((it) => nonCachedIds.includes(this.contentIdToContentSubjectId(it.id)))
            .forEach((it) => cacheInstance.cacheProgramById(this.contentIdToContentSubjectId(it.id), it));

          for (const nonCachedId of nonCachedIds) {
            if (!cacheInstance.hasCompletedProgramEntryById(nonCachedId)) {
              cacheInstance.cacheProgramById(nonCachedId, null);
            }
          }
        });
    }

    return forkJoin(ids.map((id) => cacheInstance.getCachedProgramById$(id))).pipe(map((result) => result.filter(isTruthy)));
  }

  public resolveFeedByFeedId$(feedId: string): Observable<MpxProgramResponse[]> {
    const cacheInstance = this.mpxCache;
    if (cacheInstance.hasFeed(feedId)) {
      return cacheInstance.getCachedFeed$(feedId);
    }

    const feed$ = cacheInstance.prepareFeedCache$(feedId);

    this.mpxService
      .getFeedGuids$(feedId)
      .pipe(
        map((result) => result.entries.map((it) => it.guid)),
        this.guidsIntoCachedPrograms(cacheInstance)
      )
      .subscribe((programsOfFeed) => {
        cacheInstance.cacheFeed(feedId, programsOfFeed);
      });

    return feed$;
  }

  public resolveSeriesWithSeasons$(seriesGuid: string): Observable<MpxSeries> {
    const cacheInstance = this.mpxCache;
    if (cacheInstance.hasSeries(seriesGuid)) {
      return cacheInstance.getCachedSeries$(seriesGuid);
    }
    const series$ = cacheInstance.prepareSeriesCache$(seriesGuid);

    this.resolveProgram$(seriesGuid)
      .pipe(
        switchMap((series) => {
          return forkJoin(of(series), this.mpxService.getEpisodesBySeriesId$(series.id), this.mpxService.getSeasonsBySeriesId$(series.id));
        }),
        map(([series, guids, seasons]) => [series, guids.entries.map((it) => it.guid), seasons] as const),
        switchMap(([series, episodeGuids, seasons]) => forkJoin([of(series), this.resolvePrograms$(episodeGuids), of(seasons)])),
        map(([series, episodes, seasons]) => {
          const result = new MpxSeries(series as MpxSeriesContentResponse, seasons.entries, episodes as MpxEpisodeContentResponse[]);
          cacheInstance.cacheSeries(seriesGuid, result);

          result.seasons.forEach((season) => {
            if (!this.mpxCache.seasonGuidToSeriesCache.has(season.guid)) {
              this.mpxCache.seasonGuidToSeriesCache.prepare$(season.guid);
              this.mpxCache.seasonGuidToSeriesCache.setCachedValue$(season.guid, result);
            }
          });

          return result;
        }),
        catchError((err) => {
          console.error(err);
          cacheInstance.cacheSeries(seriesGuid, null);
          return of(null);
        })
      )
      .subscribe();

    return series$;
  }

  public resolveArabicContent$(programGuid: string): Observable<MpxProgramResponse> {
    const cacheInstance = this.mpxCache;
    if (cacheInstance.hasArabicProgram(programGuid)) {
      return cacheInstance.getCachedArabicProgram$(programGuid);
    }

    const program$ = cacheInstance.prepareArabicProgramCache$(programGuid);

    this.mpxService.getArabicProgramByGuid$(programGuid).subscribe((result) => {
      const program = result.entries[0];
      cacheInstance.cacheArabicProgramIfNotStored(programGuid, program);
      return program;
    });

    return program$;
  }
  public resolveCastContent$(castId: string): Observable<MpxProgramResponse[]> {
    const cacheInstance = this.mpxCache;
    if (cacheInstance.hasCastGuids(castId)) {
      return cacheInstance.getCachedCastGuids$(castId).pipe(switchMap((guids) => this.resolvePrograms$(guids)));
    }

    const castGuids$ = cacheInstance.prepareCastGuids$(castId);

    this.mpxService
      .getGuidsByCastId$(castId)
      .pipe(
        map((response) => response.entries.map((it) => it.guid)),
        this.guidsIntoCachedPrograms(cacheInstance)
      )
      .subscribe((programs) => {
        cacheInstance.cacheCastGuids(
          castId,
          uniqueBy(
            programs.map((it) => it.guid),
            identity()
          )
        );
      });

    return castGuids$.pipe(switchMap((guids) => this.resolvePrograms$(guids)));
  }

  public resolveGenreContent$(genreTitle: string): Observable<MpxProgramResponse[]> {
    const cacheInstance = this.mpxCache;
    return this.mpxGenres$.pipe(
      switchMap((genres) => {
        const genre = genres.find((it) => it.title.toLowerCase() === genreTitle.toLowerCase());

        if (!genre) {
          throw Error(`Genre ${genreTitle} is not available in MPX`);
        }

        if (cacheInstance.hasGenreGuids(genre.guid)) {
          return cacheInstance.getCachedGenreGuids$(genre.guid).pipe(this.guidsIntoCachedPrograms(cacheInstance));
        }

        const genreGuids$ = cacheInstance.prepareGenreGuids$(genre.guid);

        this.mpxService
          .getGuidsByGenre$(genre.title)
          .pipe(
            map((response) => response.entries.map((it) => it.guid)),
            this.guidsIntoCachedPrograms(cacheInstance)
          )
          .subscribe((programs) => {
            cacheInstance.cacheGenreGuids(
              genre.guid,
              uniqueBy(
                programs.map((it) => it.guid),
                identity()
              )
            );
          });

        return genreGuids$.pipe(this.guidsIntoCachedPrograms(cacheInstance));
      })
    );
  }

  public dropCache() {
    if (!this.firstCacheDropAttempted) {
      this.firstCacheDropAttempted = true;
      return;
    }
    this.mpxCache = this.createMpxCache();
  }

  private createMpxCache(): MpxCache {
    return this.mpxCacheFactory?.() ?? new MpxCache();
  }

  public getCacheSnapshot(): MpxCacheSnapshot {
    return this.mpxCache.getCacheSnapshot();
  }

  public restoreCacheSnapshot(snapshot: MpxCacheSnapshot) {
    this.mpxCache.restoreFromCacheSnapshot(snapshot);
  }

  public resolveSeriesBySeasonGuid$(guid: string): Observable<MpxSeries> {
    if (!this.mpxCache.seasonGuidToSeriesCache.has(guid)) {
      throw Error(`Fatal error: got season [${guid}] from an unknown source`);
    }

    return this.mpxCache.seasonGuidToSeriesCache.get$(guid);
  }

  private _resolvePrograms$(programGuids: string[], cacheInstance: MpxCache): Observable<MpxProgramResponse[]> {
    if (!programGuids.length) {
      return of([]);
    }

    for (const [index, guid] of programGuids.entries()) {
      if (!cacheInstance.hasProgram(guid)) {
        cacheInstance.prepareProgramCache$(guid);
        this.guidFetchStream$.next(guid);
      }
    }

    return forkJoin(programGuids.map((it) => cacheInstance.getCachedProgram$(it))).pipe(map((result) => result.filterTruthy()));
  }

  private getMpxProgramsByGuidsRequest$(guids: string[]): Observable<MpxProgramResponse[]> {
    const requestGuids: string[][] = [];

    let currentArray: string[] = [];
    let currentLength = 0;

    for (const guid of guids) {
      currentArray.push(guid);
      currentLength += guid.length;

      if (currentLength > MPX_CHARACTER_LIMIT) {
        requestGuids.push(currentArray);
        currentArray = [];
        currentLength = 0;
      }
    }

    if (currentArray.anyItems()) {
      requestGuids.push(currentArray);
    }

    return forkJoin(requestGuids.map((request) => this.mpxService.getByGuids$(request).pipe(map((result) => result.entries)))).pipe(
      map((result) => result.flatten())
    );
  }

  private guidsIntoCachedPrograms(cacheInstance: MpxCache): OperatorFunction<string[], MpxProgramResponse[]> {
    return (source$) =>
      source$.pipe(switchMap((guids) => this._resolvePrograms$(guids, cacheInstance).pipe(map((result) => result.filterTruthy()))));
  }
}
